import React, { useState, useEffect } from 'react';
import { Drawer, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import ProgressionGraph from './ProgressionGraph';
import DeltaBadge from './DeltaBadge';
import { fetchProgressionTimeline } from '../../../actions';

const cefrColors = {
  A1: '#0d9488', A2: '#14b8a6',
  B1: '#3b82f6', B2: '#2563eb',
  C1: '#7c3aed', C2: '#6d28d9',
};
const aptColors = {
  Beginner: '#ef4444', Learner: '#f59e0b',
  Competent: '#22c55e', Advanced: '#3b82f6',
};

const LevelBadge = ({ level, type }) => {
  if (!level) return <span style={{ color: '#8c8c8c' }}>—</span>;
  const colors = type === 'communication' ? cefrColors : aptColors;
  const color = colors[level] || '#8c8c8c';
  return (
    <span style={{
      background: `${color}15`, color, border: `1px solid ${color}40`,
      borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600,
    }}>
      {level}
    </span>
  );
};

const StatCard = ({ title, level, levelType, nps, delta, taken, sent }) => (
  <div style={{
    background: '#fff', border: '1px solid #f0f0f0', borderRadius: 10,
    padding: 16, flex: 1,
  }}>
    <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', color: '#8c8c8c', letterSpacing: '0.3px', marginBottom: 12 }}>
      {title}
    </div>
    <div style={{ marginBottom: 10 }}>
      <LevelBadge level={level} type={levelType} />
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
      <span style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>{nps != null ? nps.toFixed(1) : '—'}</span>
      <span style={{ fontSize: 13, color: '#8c8c8c' }}>/ 100</span>
    </div>
    <div style={{ marginBottom: 8 }}>
      <DeltaBadge delta={delta} />
    </div>
    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
      Taken: <strong style={{ color: '#595959' }}>{taken ?? '—'}</strong> / Sent: <strong style={{ color: '#595959' }}>{sent ?? '—'}</strong>
    </div>
  </div>
);

const StudentProgressDrawer = ({ visible, onClose, student, instituteId, assessmentType }) => {
  const dispatch = useDispatch();
  const [commTimeline, setCommTimeline] = useState([]);
  const [aptTimeline, setAptTimeline] = useState([]);
  const [commDelta, setCommDelta] = useState(null);
  const [aptDelta, setAptDelta] = useState(null);
  const [loading, setLoading] = useState(false);

  const normalizedType = (assessmentType || '').toLowerCase();
  const isCommunicationType = ['communication', 'hinglish'].includes(normalizedType);
  const isAptitudeType = normalizedType === 'aptitude';

  useEffect(() => {
    if (!visible || !student?.email) return;
    const load = async () => {
      setLoading(true);
      try {
        const fetchComm = !isAptitudeType;
        const fetchApt = !isCommunicationType;
        const [commResult, aptResult] = await Promise.all([
          fetchComm ? dispatch(fetchProgressionTimeline(student.email, 'communication', instituteId)) : Promise.resolve(null),
          fetchApt ? dispatch(fetchProgressionTimeline(student.email, 'aptitude', instituteId)) : Promise.resolve(null),
        ]);
        setCommTimeline(commResult?.timeline || []);
        setAptTimeline(aptResult?.timeline || []);
        setCommDelta(commResult?.delta?.assessment || null);
        setAptDelta(aptResult?.delta?.assessment || null);
      } catch (err) {
        console.error('Error loading progression:', err);
      }
      setLoading(false);
    };
    load();
  }, [visible, student?.email, instituteId, dispatch]);

  const name = student?.name || student?.full_name || 'Student';
  const email = student?.email || '';
  const degree = student?.degree || student?.degreeName || '';
  const stream = student?.stream || student?.departmentName || '';
  const year = student?.passing_year || student?.passingYear || '';

  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const lastCommNps = commTimeline.filter(t => !t.isPractice && t.nps != null).slice(-1)[0]?.nps ?? null;
  const lastAptNps = aptTimeline.filter(t => !t.isPractice && t.nps != null).slice(-1)[0]?.nps ?? null;
  const commLevel = student?.communicationLevel || student?.cefrLevel || commTimeline.filter(t => !t.isPractice && t.level).slice(-1)[0]?.level || null;
  const aptLevel = student?.aptitudeLevel || aptTimeline.filter(t => !t.isPractice && t.level).slice(-1)[0]?.level || null;

  return (
    <Drawer
      title={null}
      placement="right"
      width={600}
      onClose={onClose}
      open={visible}
      destroyOnClose
      headerStyle={{ display: 'none' }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Student Progression</span>
        <span onClick={onClose} style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: 18, lineHeight: 1 }}>×</span>
      </div>

      <div style={{ padding: '20px', overflowY: 'auto', height: 'calc(100vh - 53px)' }}>
        {/* Student info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 700, color: '#3949ab',
          }}>
            {initials}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>{name}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{email}</div>
            {(degree || stream || year) && (
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                {[degree, stream, year].filter(Boolean).join(' • ')}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : (
          <>
            {/* Stat cards - show only relevant type */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              {!isAptitudeType && (
                <StatCard
                  title="Communication"
                  level={commLevel}
                  levelType="communication"
                  nps={student?.communicationNPS ?? student?.nps_score ?? lastCommNps}
                  delta={commDelta?.value}
                  taken={student?.communicationTaken ?? student?.commTaken ?? student?.comm_taken}
                  sent={student?.communicationSent ?? student?.commSent ?? student?.comm_sent}
                />
              )}
              {!isCommunicationType && (
                <StatCard
                  title="Aptitude"
                  level={aptLevel}
                  levelType="aptitude"
                  nps={student?.aptitudeNPS ?? student?.aptitude_nps ?? lastAptNps}
                  delta={aptDelta?.value}
                  taken={student?.aptitudeTaken ?? student?.aptTaken ?? student?.apt_taken}
                  sent={student?.aptitudeSent ?? student?.aptSent ?? student?.apt_sent}
                />
              )}
            </div>

            {/* Graphs - show only relevant type */}
            {!isAptitudeType && (
              <div style={{ marginBottom: 16 }}>
                <ProgressionGraph
                  data={commTimeline}
                  title="Communication Improvement"
                  delta={commDelta?.value}
                  height={250}
                />
              </div>
            )}
            {!isCommunicationType && (
              <div>
                <ProgressionGraph
                  data={aptTimeline}
                  title="Aptitude Improvement"
                  delta={aptDelta?.value}
                  height={250}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default StudentProgressDrawer;
